import React, { useState, useEffect } from 'react';
import { checkSetup, setupAdmin } from '../api';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5001';

const C = {
  slate900:'#0f172a', slate700:'#334155', slate500:'#64748b', slate400:'#94a3b8',
  slate200:'#e2e8f0', slate100:'#f1f5f9', slate50:'#f8fafc',
  blue600:'#2563eb', blue500:'#3b82f6', blue50:'#eff6ff',
  rose600:'#dc2626', rose50:'#fef2f2',
  indigo:'#6366f1', white:'#fff',
};

const LoginPage = ({ onLogin }) => {
  const [needsSetup, setNeedsSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Setup form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  useEffect(() => {
    // Check for Entra callback params in URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const userData = params.get('user');
    const urlError = params.get('error');

    if (token && userData) {
      localStorage.setItem('e3_auth_token', token);
      localStorage.setItem('e3_user', userData);
      window.history.replaceState({}, '', '/');
      onLogin(JSON.parse(decodeURIComponent(userData)));
      return;
    }

    if (urlError) {
      setError(decodeURIComponent(urlError));
      window.history.replaceState({}, '', '/');
    }

    const check = async () => {
      try {
        const result = await checkSetup();
        setNeedsSetup(result.needsSetup);
      } catch (e) {
        setError('Cannot connect to server. Please try again in a moment.');
      }
      setLoading(false);
    };
    check();
  }, [onLogin]);

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    try {
      const result = await setupAdmin({ email, password, first_name: firstName, last_name: lastName });
      localStorage.setItem('e3_auth_token', result.token);
      localStorage.setItem('e3_user', JSON.stringify(result.user));
      onLogin(result.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEntraLogin = () => {
    window.location.href = `${API_BASE}/api/auth/entra/login`;
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', fontSize: 14, fontWeight: 500,
    border: `1px solid ${C.slate200}`, borderRadius: 10, backgroundColor: C.white,
    color: C.slate900, outline: 'none', transition: 'border-color .15s',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600, color: C.slate500,
    marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5,
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.slate50 }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, border: `3px solid ${C.slate100}`, borderTopColor: C.indigo, borderRadius: '50%', margin: '0 auto 16px', animation: 'spin .7s linear infinite' }} />
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <div style={{ color: C.slate500, fontSize: 14, fontWeight: 500 }}>Connecting...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: C.slate50,
      backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(99,102,241,.06) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(37,99,235,.06) 0%, transparent 50%)',
    }}>
      <div style={{
        width: '100%', maxWidth: 420, padding: 40,
        backgroundColor: C.white, borderRadius: 20, border: `1px solid ${C.slate200}`,
        boxShadow: '0 4px 24px rgba(0,0,0,.06)',
      }}>
        {/* Logo / Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo.png" alt="E3 Summit" style={{
            width: 100, height: 'auto', marginBottom: 16,
          }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.slate900, letterSpacing: '-.4px' }}>
            E3 Summit
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: C.slate400 }}>
            {needsSetup ? 'Create your admin account to get started' : 'Sign in to your account'}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 10, backgroundColor: C.rose50,
            border: `1px solid #fecaca`, color: C.rose600, fontSize: 13, fontWeight: 500,
            marginBottom: 20,
          }}>
            {error}
          </div>
        )}

        {/* Setup Form — only shown on first run when no users exist */}
        {needsSetup ? (
          <form onSubmit={handleSetup}>
            <div style={{
              padding: '12px 16px', borderRadius: 10, backgroundColor: C.blue50,
              border: `1px solid #bfdbfe`, color: C.blue600, fontSize: 13, fontWeight: 500,
              marginBottom: 20,
            }}>
              First time? Create your admin account below. You can add more users after logging in.
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>First Name</label>
                <input
                  type="text" value={firstName} onChange={e => setFirstName(e.target.value)}
                  required placeholder="Duncan" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = C.indigo}
                  onBlur={e => e.target.style.borderColor = C.slate200}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Last Name</label>
                <input
                  type="text" value={lastName} onChange={e => setLastName(e.target.value)}
                  required placeholder="Broach" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = C.indigo}
                  onBlur={e => e.target.style.borderColor = C.slate200}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="you@company.com" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.indigo}
                onBlur={e => e.target.style.borderColor = C.slate200}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Password</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="Min. 6 characters" style={inputStyle}
                onFocus={e => e.target.style.borderColor = C.indigo}
                onBlur={e => e.target.style.borderColor = C.slate200}
              />
            </div>

            <button type="submit" disabled={submitting} style={{
              width: '100%', padding: '14px', borderRadius: 12, border: 'none',
              backgroundColor: C.indigo, color: C.white, fontSize: 15, fontWeight: 700,
              cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1,
              transition: 'all .15s', boxShadow: '0 2px 10px rgba(99,102,241,.3)',
            }}>
              {submitting ? 'Creating Account...' : 'Create Admin Account'}
            </button>
          </form>
        ) : (
          /* Microsoft Sign-In Only */
          <button onClick={handleEntraLogin} style={{
            width: '100%', padding: '14px', borderRadius: 12,
            border: `1px solid ${C.slate200}`, backgroundColor: C.white,
            color: C.slate900, fontSize: 15, fontWeight: 600,
            cursor: 'pointer', transition: 'all .15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
            <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </button>
        )}

        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 11, color: C.slate400 }}>
          E3 Entegral Solutions &bull; Construction Project Management
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
